import { useInfiniteQuery } from '@tanstack/react-query'
import { useContentDeliveryServiceV3 } from '../api'
import { useEffect } from 'react'
import { RoomMetadata } from '../auto-generated-clients/content-delivery-client-v3.generated'

export const ROOM_METADATA_QUERY_KEY = 'roomMetadata'
export const LIVE_TAG_QUERY_KEY = 'liveTagId'
export const LIVE_TAG = 'Live'

const ROOM_METADATA_PAGE_SIZE = 400
const MINIMUM_SEARCH_STRING_LENGTH = 2

const sortByContentMetadataId = (a: RoomMetadata, b: RoomMetadata) =>
  a.contentMetadataId.localeCompare(b.contentMetadataId)

const uniqueByContentMetadataId = (items: RoomMetadata[]) => {
  const map = new Map<string, RoomMetadata>();
  items.forEach(item => {
    if (!map.has(item.contentMetadataId)) {
      map.set(item.contentMetadataId, item);
    }
  });
  return Array.from(map.values());
};

export const useRoomMetadata = (
  searchString: string,
  companyId: string | undefined
) => {
  const cdsV3 = useContentDeliveryServiceV3()

  const searchParam =
    searchString.length > MINIMUM_SEARCH_STRING_LENGTH
      ? searchString
      : undefined

  const getRoomMetadata = ({ pageParam = 0 }: { pageParam: number }) =>
    cdsV3.getRoomMetadata(
      pageParam,
      ROOM_METADATA_PAGE_SIZE,
      companyId,
      undefined,
      undefined,
      searchParam,
      true
    )

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: [ROOM_METADATA_QUERY_KEY, companyId, searchParam],
    queryFn: getRoomMetadata,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === ROOM_METADATA_PAGE_SIZE ? allPages.length : undefined,
    enabled: !!companyId,
    initialPageParam: 0,
  })

  useEffect(() => {
    if (!isLoading && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [isLoading, hasNextPage, fetchNextPage, isFetchingNextPage])

  return {
    data: uniqueByContentMetadataId(data?.pages.flat() ?? []).sort(sortByContentMetadataId),
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  }
}
